import "./index.css";
import { useEffect, useRef, useState } from "react";
import { IconsComponent } from "../../utils/iconsv2";
import { MOODLE_STRINGS, TIPDDY_MODULES } from "../../services/tipddy-v2/state";
import meet from "../../assets/online/meet.png";
import teams from "../../assets/online/teams.png";
import zoom from "../../assets/online/zoom.png";
import { useTheme } from "../../contexts/ThemeContext";
import { readableColor, transparentize } from "polished";
import DoneIcon from "@mui/icons-material/Done";
import useActivityCompleted from "../../hooks/useActivityCompleted";
import { useAccordionScroll } from "../../hooks/useAccordionScroll";
import { FileVideoCamera } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/config";
import { requestAutoLogin } from "../../services/moodle/userauth";

const onlineicons = {
  meet: meet,
  teams: teams,
  zoom: zoom,
};

export const ResourceLabelComponent = ({ module, resource, moodle, container, isCurrentModule, idx, isModuleOpen, contentFixedBar }) => {
  const theme = useTheme();
  const resourceRef = useRef(null);
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  const { user } = useAuth();
  const { config } = useConfig();

  const { setIframeLoaded } = moodle;

  const { resourceIsTracked, resourceIsCompleted, disabledCompleteAcivity, setResourceIsTracked, setResourceIsCompleted, handleCompletedActivity } = useActivityCompleted(resource);

  // Hook para scroll coordinado con el Accordion
  const { scrollImmediate } = useAccordionScroll({
    containerId: 'scrollable-modules-wrapper',
    stickyOffset: contentFixedBar || 0,
    additionalOffset: 0 // spacing extra para recursos
  });

  const [state, setState] = useState({
    isCurrentResource: false,
    visible: false,
    isCompleted: false,
  });

  useEffect(() => {
    const isCurrent = resource.id === moodle.currentResourceId;
    const completionState = resource.completiondata?.state !== 0;
    const tracked = !!resource.isTracked;
    const completed = !!resource.isCompleted;

    setState((s) => ({
      ...s,
      visible: true,
      isCurrentResource: resource.tipddytype === "label" ? true : isCurrent,
      isCompleted: completionState,
    }));

    setResourceIsTracked(tracked);
    setResourceIsCompleted(completed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodle.currentResourceId, resource]);

  /**
   * Scroll al recurso cuando se selecciona
   * - En carga inicial: scroll al recurso actual
   * - En navegación: scroll al recurso seleccionado
   * Solo en desktop y cuando el módulo está abierto
   */
  useEffect(() => {
    const isCurrent = resource.id === moodle.currentResourceId;
    
    // Solo hacer scroll si:
    // 1. Es el recurso actual
    // 2. El módulo está abierto (isModuleOpen)
    // 3. No es mobile (desktop only)
    if (!isCurrent || !isModuleOpen || isMobile) {
      return;
    }

    // Delay para asegurar que el módulo terminó de abrirse
    // Radix Accordion típicamente usa 300ms de animación
    // Esperamos 350ms para asegurar que terminó completamente
    const scrollTimeout = setTimeout(() => {
      scrollImmediate(resourceRef.current);
    }, 350);

    return () => clearTimeout(scrollTimeout);
  }, [moodle.currentResourceId, isModuleOpen, isMobile, scrollImmediate, resource.id]);

  const scrollToTopIfMobile = (isMobile) => {
    if (!isMobile) return;
    const wrapper = document.getElementById("course-wrapper");
    if (wrapper) wrapper.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleResourceSelected = async () => {
    if (resource.tipddytype === "label") return;

    const type = resource.tipddytype;

    // PROGRAMS (direct embed)
    if (type === MOODLE_STRINGS.PROGRAMS) {
      moodle.setPageEmbed(resource.tipddyurl);
      return scrollToTopIfMobile(isIOS || isMobile);
    }

    // ONLINE direct external
    if (type === MOODLE_STRINGS.ONLINE && moodle.selectedView === 2) {
      if (!resource.embed) {
        return window.open(resource.onlineurl);
      }

      setIframeLoaded(false);
      await moodle.requestPageEmbed({
        pageurl: resource.url,
        module,
        resource,
        course_id: moodle.currentCourseId,
      });

      return scrollToTopIfMobile(isIOS || isMobile);
    }

    // ONLINE view 1 (only prepare iframe)
    if (type === MOODLE_STRINGS.ONLINE && moodle.selectedView !== 2) {
      setIframeLoaded(false);
      return;
    }

    // Normal Moodle resources
    // El scroll se maneja automáticamente en el useEffect que monitorea currentResourceId

    /**
     * Caso especial: recurso tipo "customcert" en dispositivos iOS (iPhone/iPad/iPod).
     *
     * El comportamiento por defecto es cargar el certificado dentro de un iframe autenticado
     * via Moodle. Sin embargo, en navegadores basados en Safari (iOS), el iframe renderiza
     * el PDF con un zoom excesivo que no puede controlarse desde la app.
     *
     * Solución: cuando el usuario está en iOS, en vez de abrir el iframe, se usa el endpoint
     * de la API de Tipddy (el mismo que utiliza la página de Historial/Progress) para obtener
     * el PDF directamente y abrirlo en una nueva pestaña. Esto evita el problema de zoom ya
     * que el PDF se sirve como archivo descargable/visualizable nativo del sistema.
     */
    if (isIOS && resource.tipddyname === TIPDDY_MODULES.customcert) {
      const { key } = await requestAutoLogin({
        username: user.username,
        baseUrl: config.baseURL,
        tipddytoken: config.token,
      });
      const certUrl = `https://api-interfaz-alumnos.tipddy.cl/history/cert/${resource.id}?userid=${user.id}&domain=${encodeURIComponent(config.baseURL)}&userkey=${key}`;
      window.open(certUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setIframeLoaded(false);
    await moodle.requestPageEmbed({
      pageurl: resource.url,
      module,
      resource,
      course_id: moodle.currentCourseId,
    });

    scrollToTopIfMobile(isIOS || isMobile);
  };

  if (resource.tipddytype === MOODLE_STRINGS.PROGRAMS && moodle.selectedView !== 1) return null;
  if (resource.tipddytype === TIPDDY_MODULES.schedule) return null;
  if (resource.tipddyname === "summary") return null;
  if (resource.tipddyname === TIPDDY_MODULES.humhub) return null;

  /*
   * El siguiente condicional es para evitar mostrar recursos ocultos
   * uservisible tiende ser igual a 0, cuando el usuario no pertenece al grupo del curso que puede ver el recurso
   * visible tiende ser igual a 0, cuando el recurso esta oculto (roles como administrador pueden visualizarlo)
   * visibleoncoursepage tiende ser igual a 0, cuando el recurso esta oculto pero disponible
   */
  // if (!resource.uservisible || resource.visible === 0 || resource.visibleoncoursepage === 0) {
  //   return null;
  // }

  const { isCurrentResource } = state;

  return (
    <div
      ref={resourceRef}
      className={`resource-label-wrapper fade-slide-in ${isCurrentResource ? "current-resource-item" : ""}`}
      style={{
        backgroundColor: isCurrentResource && (theme.mode === "light" ? "rgb(0 0 0 / 0.1)" : "rgb(0 0 0 / 0.3)"),
        paddingLeft: "26px",
        display: "flex",
        alignItems: "center",
        // marginRight: "15px",
        paddingRight: "15px",
      }}
    >

      {/* ICONS */}
      {moodle.selectedView !== 2 && (
        <div style={{ marginLeft: "auto" }} className="min-w-[20px] mr-4">
          {resource.name.includes("-Clase") && resource.tipddyname === "page" ? (
            <FileVideoCamera size={22} color={theme.mode === "light" ? theme.colors.background_dark : "#fff"} opacity={0.75} strokeWidth={1.75}  />
          ) : (
            <IconsComponent resource={resource.tipddyname} color={theme.mode === "light" ? theme.colors.background_dark : "#fff"} />
          )}
        </div>
      )}
      {moodle.selectedView === 2 && resource.tipddyname && (
        <div style={{ marginLeft: "auto" }}>
          <img alt="...icon" src={onlineicons[`${resource.tipddyname}`]} width="18px" style={{ marginRight: "12px", marginBottom: 3 }} />
        </div>
      )}
      

      {/* RESOURCE NAME */}
      <div className="grow cursor-pointer p-[15px_0px]" style={{ padding: "15px 0px" }} onClick={handleResourceSelected}>
        <p
          className="resource-container"
          style={{
            margin: 0,
            marginLeft: resource.indent * 5,
            color: readableColor(theme.colors.backgroundColorDetailCourse),
            fontSize: resource.tipddytype === MOODLE_STRINGS.ONLINE || resource.tipddytype === MOODLE_STRINGS.PROGRAMS ? "15px" : isCurrentResource ? "14px" : "14px",
            fontWeight: isCurrentResource ? "700" : "400",
          }}
        >
          {resource.name}
        </p>
        {resource.tipddytype === MOODLE_STRINGS.ONLINE && moodle.selectedView === 0 && (
          <p
            className="resource-container"
            style={{
              color: "#333",
              margin: 0,
              fontSize: "10px",
            }}
          >
            Recurso disponible en clases en linea
          </p>
        )}
      </div>
      
      {/* TRACK / COMPLETION ICON */}
      <div disabled={disabledCompleteAcivity} className="z-50 mr-[15px] p-[15px_0px]" onClick={handleCompletedActivity}>
        {resourceIsTracked ? (
          resourceIsCompleted ? (
            <div
              className="rounded-full border-2 size-11 flex justify-center items-center cursor-pointer hover:brightness-[.80] transition-all"
              style={{
                borderColor: transparentize(0.5, readableColor(theme.colors.backgroundColorDetailCourse)),
                backgroundColor: "#FE5100",
              }}
            >
              <DoneIcon
                sx={{
                  color: readableColor(theme.colors.backgroundColorDetailCourse),
                  fontSize: 20,
                  opacity: resourceIsCompleted ? 0.8 : 0.2,
                }}
              />
            </div>
          ) : (
            <div
              className="rounded-full border-2 size-11 flex justify-center items-center cursor-pointer hover:brightness-[.80] transition-all"
              style={{ borderColor: transparentize(0.3, readableColor(theme.colors.backgroundColorDetailCourse)) }}
            >
              <DoneIcon
                sx={{
                  color: readableColor(theme.colors.backgroundColorDetailCourse),
                  fontSize: 22,
                  /* opacity: resourceIsCompleted ? 0.8 : 0.2, */
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              />
            </div>
          )
        ) : (
          <div className="w-11 h-full"></div>
        )}
      </div>
      
    </div>
  );
};
